import { intToDate } from '@/helpers/dateTime';

/**
 * Helpers for dashboard line series that must show a gap — not 0 — where a metric has no
 * value for the bucket (VSER-14215). Highcharts renders a null point as a break in the
 * line as long as `connectNulls` stays at its default (false).
 */

/**
 * True when a YYYYMMDD operation day is after today (local time). A metric for a day that
 * has not happened yet has no value, so the series leaves that point blank.
 * @param {number} operationDay - e.g. 20260911
 * @returns {boolean}
 */
export function isFutureOperationDay(operationDay) {
  if (!operationDay) return false;
  const itemDate = intToDate(operationDay);
  if (!(itemDate instanceof Date) || Number.isNaN(itemDate.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  itemDate.setHours(0, 0, 0, 0);
  return itemDate > today;
}

/**
 * Visible line/spline series of a shared tooltip's chart that have no point at the
 * hovered x. Highcharts leaves null points out of the tooltip's `points`, so a metric
 * that is undefined for the bucket would otherwise vanish from the tooltip without
 * explanation; callers list these series as N/A instead.
 * @param {Array} points - `this.points` inside a shared tooltip formatter
 * @returns {Array} Highcharts series objects
 */
export function lineSeriesWithoutPoint(points = []) {
  const chart = points[0]?.series?.chart;
  if (!chart) return [];
  return chart.series.filter(
    (series) =>
      series.visible &&
      (series.type === 'spline' || series.type === 'line') &&
      !points.some((point) => point.series === series),
  );
}
